import type { Jet } from '~/jet';
import type { LoggerFactory } from '@amp/web-apps-logger';
import type { GetTrackMetadataAction } from '~/actions/get-track-metadata-action';
import { makeTrackMetadataIntent } from '@jet-app/music-classical/intents/track-metadata/TrackMetadataIntent';
import {
    queueMetadata,
    setQueueMetadata,
    updateQueueMetadata,
} from '~/stores/queue-metadata';
import { get } from 'svelte/store';
import { selectedItemIndex } from '~/stores/selected-item-index';

export type Dependencies = {
    jet: Jet;
    logger: LoggerFactory;
};

/**
 * Fetches additional metadata for tracks in the queue
 */
export function registerHandler(dependencies: Dependencies) {
    const { jet, logger } = dependencies;

    const log = logger.loggerFor(
        'jet/action-handlers/get-track-metadata-action',
    );

    jet.onAction(
        'getTrackMetadataAction',
        async (action: GetTrackMetadataAction) => {
            log.info('received getTrackMetadataAction:', action);
            if (action?.trackIds?.length) {
                const idsToFetch = action.trackIds.filter(
                    (id) => !get(queueMetadata)?.get(id),
                );

                if (idsToFetch.length) {
                    log.info(
                        `fetching track metadata for ${idsToFetch.toString()}`,
                    );

                    const intent = makeTrackMetadataIntent({
                        trackIds: action.trackIds,
                        queue: action.queue,
                    });
                    const intentData = await jet.dispatch(intent);

                    if (idsToFetch.length === action.trackIds.length) {
                        // Completely new queue, reinit the store with these valeus
                        setQueueMetadata(new Map(Object.entries(intentData)));
                    } else {
                        // Modified queue, keep existing values and add new ones to the store
                        // OR non-Classical items exist in the queue
                        updateQueueMetadata(
                            new Map(Object.entries(intentData)),
                        );
                    }

                    // Reset selected item index value for Queue when
                    // changing the contents of the playback queue
                    selectedItemIndex.set(null);
                } else {
                    log.info(
                        `using previously stored metadata for ${action.trackIds}`,
                    );
                }
            } else {
                log.warn('no ids given to fetch track metadata');
            }

            return 'performed';
        },
    );
}
